/**
 * StylesBlock.js — corregido v2
 *
 * Fix principal: inyectar el CSS en document.head via useEffect en vez de
 * como elemento <style> en el árbol de React.
 *
 * El problema anterior: el <style dangerouslySetInnerHTML> se insertaba dentro
 * del árbol de componentes de CraftJS. Esto causaba que los estilos de clases
 * como .search-icon, .search-input, .card, .grid, etc. no se aplicaran
 * correctamente porque el Shadow DOM o el orden de inserción los dejaba sin efecto.
 *
 * La solución: useEffect inserta y limpia el <style> en document.head,
 * igual que lo haría el HTML original, garantizando que todas las reglas CSS
 * (incluyendo :hover, ::placeholder, @media) afecten a los nodos del editor.
 */
import React, { useEffect } from 'react';
import { useNode } from '@craftjs/core';
import { FormControl, FormLabel, Typography, Box, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Icons from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  editorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: '#fffde7',
    border: '1px dashed #fbc02d',
    borderRadius: 4,
    cursor: 'pointer',
    userSelect: 'none',
    marginBottom: theme.spacing(1),
    '&:hover': { backgroundColor: '#fff9c4' },
  },
  editorIcon:  { color: '#f57c00', fontSize: '1rem' },
  editorLabel: { fontSize: '0.75rem', color: '#795548', fontWeight: 600 },
  settings:    { padding: theme.spacing(2) },
  statsRow:    { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginBottom: theme.spacing(2) },
  chip:        { height: 20, fontSize: '0.68rem' },
  hint: {
    fontSize: '0.72rem', color: '#888',
    marginBottom: theme.spacing(1), lineHeight: 1.4,
  },
  cssTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 200,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
    '&:focus': { borderColor: '#2196f3', backgroundColor: '#fff' },
  },
}));

// ID único para el <style> que inyectamos, así podemos limpiarlo al desmontar
const STYLE_ELEMENT_ID = 'craftjs-imported-styles';

export const StylesBlock = ({ css }) => {
  const classes = useStyles();
  const {
    connectors: { connect, drag },
  } = useNode((node) => ({ selected: node.events.selected }));

  // FIX: Inyectar CSS en document.head para que aplique globalmente en el editor.
  // Esto es equivalente a tener el <style> en el <head> del HTML original.
  useEffect(() => {
    if (!css) return;

    // Reutilizar el elemento si ya existe (evita duplicados en hot-reload)
    let styleEl = document.getElementById(STYLE_ELEMENT_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ELEMENT_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;

    // Cleanup: quitar los estilos cuando el componente se desmonta
    // (ej: al cambiar de página en el editor)
    return () => {
      const el = document.getElementById(STYLE_ELEMENT_ID);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [css]);

  const lineCount = css ? css.split('\n').length : 0;
  const ruleCount = css ? (css.match(/\{/g) || []).length : 0;

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={classes.editorBanner}
      title="Estilos globales de la plantilla importada. Haz clic para editar."
    >
      <Icons.Code className={classes.editorIcon} />
      <span className={classes.editorLabel}>
        Estilos globales — {ruleCount} reglas · {lineCount} líneas
      </span>
      <Icons.Edit style={{ fontSize: '0.8rem', color: '#bbb', marginLeft: 'auto' }} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const StylesBlockSettings = () => {
  const classes = useStyles();
  const { actions: { setProp }, css } = useNode((node) => ({ css: node.data.props.css }));

  const lineCount = css ? css.split('\n').length : 0;
  const ruleCount = css ? (css.match(/\{/g) || []).length : 0;
  const sizeKb    = css ? (new Blob([css]).size / 1024).toFixed(1) : '0';

  return (
    <div className={classes.settings}>
      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700 }}>
        Estilos globales
      </Typography>

      <Box className={classes.statsRow}>
        <Chip className={classes.chip} size="small" label={`${ruleCount} reglas CSS`} />
        <Chip className={classes.chip} size="small" label={`${lineCount} líneas`} />
        <Chip className={classes.chip} size="small" label={`${sizeKb} KB`} />
      </Box>

      <Typography className={classes.hint}>
        CSS global inyectado en el editor. Contiene hover effects, transitions,
        animaciones y media queries. Edítalo aquí o elimina el componente para
        usar solo los estilos individuales de cada nodo.
      </Typography>

      <FormControl fullWidth>
        <FormLabel>CSS</FormLabel>
        <textarea
          className={classes.cssTextarea}
          value={css || ''}
          onChange={(e) => setProp((props) => (props.css = e.target.value))}
          spellCheck={false}
          autoComplete="off"
          style={{ marginTop: 8 }}
        />
      </FormControl>
    </div>
  );
};

StylesBlock.craft = {
  props: { css: '' },
  related: { settings: StylesBlockSettings },
};